/******************************************************************************************
 * This file holds the APIs for Penetapan by Pengawas Sekolah for Plt KS
 ******************************************************************************************/

import { apiPaths } from 'api/kinerja/kepala-sekolah/config'
import { useKinerjaApi, useKinerjaMutationApi } from 'api/kinerja/utils/api'

import type { CommonResponse } from 'types/api'
import type { PelaksanaanError } from 'types/kinerja-ks-pelaksanaan'
import type {
  TPayloadPenilaian,
  TPayloadPenilaianForDelivery,
  TPenilaianFilterStatus,
  TResponsePenilaian,
  TResponsePenilaianCount,
  TResponsePenilaianForDelivery,
  TResponsePenilaianSummary,
} from 'types/kinerja-ps'
import type { UseMutationOptions, UseQueryOptions } from 'react-query'

type TPenilaianPsProp = {
  name?: string
  status?: TPenilaianFilterStatus[]
  confirmedOnly?: boolean
}

export const API_GET_KS_PENILAIAN_BY_PS_TAB_GURU_QUERY_KEY =
  'API_GET_KS_PENILAIAN_BY_PS_TAB_GURU_QUERY_KEY'

export const API_GET_KS_PENILAIAN_BY_PS_SUMMARY_TAB_GURU_QUERY_KEY =
  'API_GET_KS_PENILAIAN_BY_PS_SUMMARY_TAB_GURU_QUERY_KEY'

export const API_GET_KS_PENILAIAN_COUNT_BY_PS_TAB_GURU_QUERY_KEY =
  'API_GET_KS_PENILAIAN_COUNT_BY_PS_TAB_GURU_QUERY_KEY'

export function useApiGetPenilaianPsTabGuru(
  params: TPenilaianPsProp,
  opts?: UseQueryOptions<TResponsePenilaian>
) {
  const nameParam = params?.name || undefined

  const status: TPenilaianFilterStatus[] = params?.status || ['ALL']
  const statusParam = status ? status?.join(',') : undefined

  // Intentionally exclude `false` from the query string so the API can handle it according to its default behavior
  const confirmedOnlyParam = params?.confirmedOnly === true || undefined

  return useKinerjaApi<TResponsePenilaian>(
    apiPaths.guruPenilaianPltKsPengawasSekolah,
    {
      axiosConfig: {
        params: {
          name: nameParam,
          statusPengiriman: statusParam,
          confirmedOnly: confirmedOnlyParam,
        },
      },
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      queryKey: [
        API_GET_KS_PENILAIAN_BY_PS_TAB_GURU_QUERY_KEY,
        nameParam,
        statusParam,
        confirmedOnlyParam,
      ],
    }
  )
}

export function useApiGetPenilaianSummaryPsTabGuru(
  opts?: UseQueryOptions<TResponsePenilaianSummary>
) {
  return useKinerjaApi<TResponsePenilaianSummary>(
    `${apiPaths.guruPenilaianPltKsPengawasSekolah}/summary`,
    {
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      queryKey: [API_GET_KS_PENILAIAN_BY_PS_SUMMARY_TAB_GURU_QUERY_KEY],
    }
  )
}

export function useApiGetPenilaianCountPsTabGuru(
  opts?: UseQueryOptions<TResponsePenilaianCount>
) {
  return useKinerjaApi<TResponsePenilaianCount>(
    `${apiPaths.guruPenilaianPltKsPengawasSekolah}/count`,
    {
      queryConfig: {
        useErrorBoundary: false,
        ...opts,
      },
      queryKey: [API_GET_KS_PENILAIAN_COUNT_BY_PS_TAB_GURU_QUERY_KEY],
    }
  )
}

export function useApiPostPenilaianForDeliveryTabGuru(
  opts?: UseMutationOptions<
    CommonResponse<TResponsePenilaianForDelivery>,
    PelaksanaanError,
    Pick<TPayloadPenilaianForDelivery, 'id'>
  >
) {
  return useKinerjaMutationApi(apiPaths.guruPenilaianPltKsPengawasSekolah, {
    axiosConfig: {
      method: 'POST',
    },
    mutationConfig: {
      useErrorBoundary: false,
      ...opts,
    },
  })
}

export function useApiPatchPenilaianPsTabGuru(
  opts?: UseMutationOptions<
    CommonResponse<any>,
    PelaksanaanError,
    TPayloadPenilaian
  >
) {
  return useKinerjaMutationApi(
    `${apiPaths.guruPenilaianPltKsPengawasSekolah}/override`,
    {
      axiosConfig: {
        method: 'PATCH',
      },
      mutationConfig: {
        useErrorBoundary: false,
        ...opts,
      },
    }
  )
}
